/** @param {string|Date} date */
export function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Prod policies list + building policy tab: hide flag after coverage end + N days.
 * @param {string|Date} coverageEndDate
 * @param {number} [days=120]
 * @param {number} [now=Date.now()]
 */
export function isPastCoverageEndPlusDays(coverageEndDate, days = 120, now = Date.now()) {
  if (!coverageEndDate) return false;
  const end = new Date(coverageEndDate);
  if (Number.isNaN(end.getTime())) return false;
  return now > addDays(end, days).getTime();
}

/**
 * Display value for policy_flag column (prod FE suppression — BE still stores flag).
 * @param {object} policy
 * @param {{ clearOnSameCoverageDay?: boolean, now?: number }} [options]
 * @returns {'green'|'red'|''}
 */
export function resolvePolicyFlagForDisplay(
  policy,
  { clearOnSameCoverageDay = false, now = Date.now() } = {},
) {
  let flag = policy?.policy_flag || '';
  if (!flag) return '';

  if (isPastCoverageEndPlusDays(policy?.coverage_end_date, 120, now)) {
    return '';
  }

  if (
    clearOnSameCoverageDay &&
    policy?.coverage_start_date &&
    policy?.coverage_end_date &&
    policy.coverage_start_date === policy.coverage_end_date
  ) {
    return '';
  }

  return flag === 'green' || flag === 'red' ? flag : '';
}

/** Prod: File a Claim disabled when not cancellable or >120 days after coverage end. */
export function isClaimActionDisabled(policy) {
  if (!policy?.is_cancellable) return true;
  return isPastCoverageEndPlusDays(policy.coverage_end_date, 120);
}

/** App types eligible for self-service cancellation from the policies list (prod parity). */
export const POLICY_CANCELLABLE_APP_TYPES = ['Auto Enroll', 'LDR Commercial'];

export function isPolicyCancellableAppType(appType) {
  return POLICY_CANCELLABLE_APP_TYPES.includes(appType);
}

function isCoverageEndDatePast(coverageEndDate, now = new Date()) {
  if (!coverageEndDate) return false;
  const endDateOnly = String(coverageEndDate).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(endDateOnly)) return false;
  const today = now.toISOString().slice(0, 10);
  return endDateOnly < today;
}

function isCoverageStartDateFuture(coverageStartDate, now = new Date()) {
  if (!coverageStartDate) return false;
  const startDateOnly = String(coverageStartDate).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDateOnly)) return false;
  const today = now.toISOString().slice(0, 10);
  return startDateOnly > today;
}

function isTruthyLeaseFlag(value) {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();
  return normalized === 'true' || normalized === '1';
}

/**
 * Policies list "Active" column — lease flag plus policy still in force.
 * Expired, cancelled, or not-yet-started coverage shows No even when PMS lease flag is true.
 * @returns {'true'|'false'}
 */
export function resolvePolicyActiveForDisplay(policy, now = new Date()) {
  if (!policy) return 'false';
  if (policy.is_cancelled === true || isTruthyLeaseFlag(policy.is_cancelled)) {
    return 'false';
  }
  if (isCoverageEndDatePast(policy.coverage_end_date, now)) {
    return 'false';
  }
  if (isCoverageStartDateFuture(policy.coverage_start_date, now)) {
    return 'false';
  }
  return isTruthyLeaseFlag(policy.active_lease) ? 'true' : 'false';
}

export function formatPolicyActiveLabel(policy, now = new Date()) {
  return resolvePolicyActiveForDisplay(policy, now) === 'true' ? 'Yes' : 'No';
}

/** Prod: Cancel Policy disabled for AE/LDR Commercial when coverage end is in the past. */
export function isCancelPolicyActionDisabled(policy) {
  if (!policy?.is_cancellable) return true;
  if (!isPolicyCancellableAppType(policy?.app_type)) return true;
  return isCoverageEndDatePast(policy?.coverage_end_date);
}

/** Tooltip copy when Cancel Policy action is disabled. */
export function getCancelPolicyDisabledReason(policy) {
  if (!policy) return 'Not available for this policy';
  const stage = policy?.stage;
  const actionsAvailable =
    Boolean(stage) && (stage.includes('02') || stage === '05 - Policy Issued');
  if (!actionsAvailable) return 'Cancel Policy — Not available for this policy stage';
  if (!policy.is_cancellable) return 'Cancel Policy — This policy cannot be cancelled';
  if (!isPolicyCancellableAppType(policy.app_type)) {
    return 'Cancel Policy — Only available for Guarantor Waiver Program and LDR Commercial policies';
  }
  if (isCoverageEndDatePast(policy.coverage_end_date)) {
    return 'Cancel Policy — Coverage has ended for this policy';
  }
  return null;
}

export const POLICY_FLAG_LABELS = {
  green: 'Possible Claim',
  red: 'Possible Cancellation',
};

export const POLICY_FLAG_TOOLTIPS = {
  green:
    'Possible Claim: A chargecode and/or lease cancellation has been identified in your property management system (PMS) and a move-out reason of "Skip/Eviction/Failure to Pay" has been identified. If this move-out reason is correct or if the chargecode was dropped due to anything that would result in Missed Rent, please proceed with filing a claim. If you would like to cancel the policy, please be advised that canceling a policy before a claim is submitted would result in no coverage due to the policy being canceled. If there are any discrepancies, please review the lease status in your PMS or contact us at partnerships@leapeasy.com',
  red: 'Possible Cancellation: A chargecode and/or lease cancellation has been identified in your property management system (PMS) and a move-out reason of "Skip/Eviction/Failure to Pay" has not been identified. If your selected move-out reason was due to anything that would result in Missed Rent, please proceed with filing a claim. If you would like to cancel the policy, please be advised that canceling a policy before a claim is submitted would result in no coverage due to the policy being canceled. If there are any discrepancies, please review the lease status in your PMS or contact us at partnerships@leapeasy.com',
};
